/**
 * useFocusFromURL — Vincent B590.b (Eric 2026-07-29 SPEC STRICK)
 *
 * Hook global : lit `?focus=<id>` de l'URL après montage, puis polls
 * pendant maxWaitMs à la recherche d'un élément `[data-focus-target="<id>"]`.
 * Dès qu'il le trouve, il :
 *   1. scrolle en douceur vers l'élément.
 *   2. applique une classe de highlight (ring bleu Knoria) pendant 3 s.
 *
 * Utilisé pour transformer les liens de favoris (Q&R, Objections, etc.) en
 * navigation « atterrir DIRECTEMENT sur l'item », pas juste ouvrir l'onglet.
 *
 * Usage :
 *   useFocusFromURL();     // dans un composant page qui contient les items.
 *   ...
 *   <li data-focus-target={item.id}>…</li>
 */
import { useEffect } from "react";

const CLASS_HIGHLIGHT = "kn-focus-highlight";
const CSS_INJECTED_FLAG = "__kn_focus_css_injected__";

function ensureCss() {
  if (typeof document === "undefined") return;
  if (document.documentElement.dataset[CSS_INJECTED_FLAG]) return;
  const style = document.createElement("style");
  style.setAttribute("data-kn-focus", "1");
  style.textContent = `
    .${CLASS_HIGHLIGHT} {
      outline: 3px solid #002FA7 !important;
      outline-offset: 4px;
      border-radius: 8px;
      transition: outline-color 300ms ease, box-shadow 300ms ease;
      box-shadow: 0 0 0 6px rgba(0, 47, 167, 0.15);
      animation: kn-focus-pulse 1s ease-out 3;
    }
    @keyframes kn-focus-pulse {
      0%, 100% { box-shadow: 0 0 0 6px rgba(0, 47, 167, 0.15); }
      50%      { box-shadow: 0 0 0 14px rgba(0, 47, 167, 0.05); }
    }
  `;
  document.head.appendChild(style);
  document.documentElement.dataset[CSS_INJECTED_FLAG] = "1";
}

export function useFocusFromURL(deps = [], { maxWaitMs = 6000, pollMs = 250 } = {}) {
  useEffect(() => {
    ensureCss();
    const params = new URLSearchParams(window.location.search);
    const target = params.get("focus");
    if (!target) return;

    let cancelled = false;
    let elapsed = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-focus-target="${CSS.escape(target)}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) { /* noop */ }
        el.classList.add(CLASS_HIGHLIGHT);
        setTimeout(() => el.classList.remove(CLASS_HIGHLIGHT), 3200);
        return;
      }
      elapsed += pollMs;
      if (elapsed >= maxWaitMs) return;
      setTimeout(tick, pollMs);
    };
    // délai initial pour laisser l'onglet se rendre
    const initial = setTimeout(tick, 400);
    return () => { cancelled = true; clearTimeout(initial); };
    // Vincent P1.9.c (Eric 2026-07-31) — Ce hook accepte un array `deps`
    // variadique du caller (contrat public documenté ci-dessus). ESLint ne
    // peut pas statiquement vérifier ces deps. `maxWaitMs`/`pollMs` sont des
    // constantes de config lues UNE fois au montage — les inclure forcerait
    // un re-run à chaque re-render sans bénéfice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
